import React, { useContext, useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import RosterSubmissionsTable from "./RosterSubmissionsTable";
import Theme from "../../Theme";
import CountryContext from "../../contexts/CountryContext";
import WBCYearsContext from "../../contexts/WBCYearsContext";
import WBCTeamApi from "../../httpClients/WBCTeamApi";
import useTournamentType from "../hooks/useTournamentType";
import {
  MAIN,
  MAIN_ID,
  MAIN_TOURNAMENT_STATUSES,
  QUALIFYING_ID,
  QUALIFYING_TOURNAMENT_STATUSES
} from "../../constants/TournamentTypeConstants";

const Content = styled.div.attrs(() => ({
  className: "p-4"
}))`
  text-align: left;
  height: 80vh;
`;

const Header = styled.h2`
  font-family: Helvetica;
  font-size: 24px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: ${Theme.wbcBlue};
  margin-bottom: 1rem;
`;

const RosterSubmissionsForm = () => {
  // get current year and current tournament type
  const { selectedWbcYear } = useContext(WBCYearsContext);
  const { countries } = useContext(CountryContext).state;
  const { tournamentType, isMainTournament, tournamentTypeId } = useTournamentType();
  const currentStatuses = isMainTournament ? MAIN_TOURNAMENT_STATUSES : QUALIFYING_TOURNAMENT_STATUSES;

  const [fedTeams, setFedTeams] = useState([]);
  const currentYear = selectedWbcYear?.label;
  const currentTournamentType = isMainTournament ? "Main Tournament" : "Qualifying Tournament";

  // Convert timestamp to formatted date string
  const formatTimestamp = timestamp => {
    if (!timestamp) return null;
    return new Date(timestamp).toLocaleDateString();
  };

  // Transform federation team data for table display
  const createTeamTableRow = (fed, country, teamTotals) => {
    return {
      id: fed?.teamId,
      status: fed?.currentState?.statusId,
      comments: fed?.comments,
      fedTeamName: country?.fedTeamName,
      intListTotal: teamTotals?.interestListCount || 0,
      intListFinalizedDate: formatTimestamp(fed?.finalizedStatus?.finalizedInterestDate),
      provListTotal: teamTotals?.provisionalListCount || 0,
      provListFinalizedDate: formatTimestamp(fed?.finalizedStatus?.finalizedProvisionalDate),
      finalRosterTotal: teamTotals?.finalRosterCount || 0,
      finalRosterFinalizedDate: formatTimestamp(
        isMainTournament
          ? fed?.finalizedStatus?.mainRosterFinalizedDate
          : fed?.finalizedStatus?.qualifyingRosterFinalizedDate
      )
    };
  };

  const rosterMaxValues = useMemo(() => {
    if (!selectedWbcYear) {
      return null;
    }

    const settings = selectedWbcYear[`${tournamentType}TournamentSettings`];
    const generalSettings = selectedWbcYear.generalRosterSettings;

    if (!settings?.rosterNumbers || !generalSettings) {
      return null;
    }
    return {
      finalRosterMax: settings.rosterNumbers.finalRosterMax,
      provisionalMax: settings.rosterNumbers.provisionalMax,
      intListMax: generalSettings.interestListMax
    };
  }, [selectedWbcYear, tournamentType]);

  useEffect(() => {
    if (selectedWbcYear?.year && countries && tournamentTypeId) {
      Promise.all([
        WBCTeamApi.getRosterTotals(selectedWbcYear.year, tournamentTypeId),
        WBCTeamApi.getFederationTeamsByYear(selectedWbcYear?.year)
      ])
        .then(([totalsRes, federationTeamsRes]) => {
          const feds = federationTeamsRes.map(fed => {
            const country = countries?.find(c => c?.countryId === fed?.countryId);
            const teamTotals = totalsRes?.find(t => t.fedTeamId === fed?.teamId);
            return createTeamTableRow(fed, country, teamTotals);
          });

          const filteredAndSortedFeds = feds
            .filter(fed => currentStatuses.includes(fed.status))
            .sort((a, b) => a.fedTeamName?.localeCompare(b.fedTeamName));
          setFedTeams(filteredAndSortedFeds);
        })
        .catch(error => {
          console.error("Error fetching roster data:", error);
          setFedTeams([]);
        });
    }
  }, [selectedWbcYear, countries, tournamentTypeId, currentStatuses]);

  const handleUpdateComments = (teamId, comments) => {
    WBCTeamApi.updateTeamComments(teamId, comments);

    setFedTeams(prevTeams => prevTeams.map(team => (team.id === teamId ? { ...team, comments } : team)));
  };

  return (
    <Content>
      <Header>{`${currentYear}  ${currentTournamentType}`}</Header>
      {/* adding checks to prevent rendering if data is not ready */}
      {rosterMaxValues && fedTeams.length > 0 && fedTeams[0].fedTeamName ? (
        <RosterSubmissionsTable
          data={fedTeams}
          intListMax={rosterMaxValues.intListMax}
          provisionalMax={rosterMaxValues.provisionalMax}
          finalRosterMax={rosterMaxValues.finalRosterMax}
          onUpdateComments={handleUpdateComments}
        />
      ) : (
        <div>Loading Roster Data...</div>
      )}
    </Content>
  );
};

export default RosterSubmissionsForm;
